import { Link } from "react-router-dom";
import { useState } from "react";
import { Star, Check, Zap } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const [added, setAdded] = useState(false);
  const discount = product.compare_at
    ? Math.round((1 - product.price / product.compare_at) * 100)
    : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    add(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  const lowStock = typeof product.stock === "number" && product.stock <= 15;
  // Un producto sin precio confirmado se anuncia, pero no se vende: el precio
  // real todavía no está definido y un "Añadir" aquí crearía un pedido a $0.
  const sinPrecio = !(product.price > 0);

  return (
    <Link
      to={`/producto/${product.id}`}
      className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-drelo-primary/40 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col animate-rise"
      style={{ animationDelay: `${Math.min(index * 50, 400)}ms` }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-slate-50">
        <img src={product.image} alt={product.title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {discount > 0 && (
          <span className="absolute top-2 left-2 bg-drelo-urgency text-white text-xs font-black px-2 py-1 rounded-md shadow">
            -{discount}%
          </span>
        )}
        {product.badge && (
          <span className="absolute top-2 right-2 bg-drelo-deal text-drelo-ink text-[10px] font-black px-2 py-1 rounded-md tracking-wide">
            {product.badge}
          </span>
        )}
        {product.is_flash && (
          <span className="absolute bottom-2 left-2 bg-drelo-ink/80 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded-md flex items-center gap-1">
            <Zap className="w-3 h-3 text-drelo-deal" /> RELÁMPAGO
          </span>
        )}
      </div>

      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-sm font-semibold leading-tight line-clamp-2 min-h-[2.5rem]">{product.title}</h3>

        <div className="flex items-center gap-1 mt-1.5 text-xs text-drelo-muted">
          <Star className="w-3.5 h-3.5 fill-drelo-deal text-drelo-deal" />
          <span className="font-bold text-drelo-ink">{product.rating}</span>
          <span>({product.reviews_count})</span>
          <span className="ml-auto text-drelo-urgency font-bold">🔥 {product.sold} vendidos</span>
        </div>

        <div className="flex items-end gap-2 mt-2">
          {sinPrecio ? (
            <span className="font-display font-black text-lg text-drelo-muted uppercase tracking-tight">Próximamente</span>
          ) : (
            <>
              <span className="font-display font-black text-2xl text-drelo-primary">${product.price.toFixed(2)}</span>
              {product.compare_at && (
                <span className="text-sm text-drelo-muted line-through mb-0.5">${product.compare_at.toFixed(2)}</span>
              )}
            </>
          )}
        </div>

        {lowStock && !sinPrecio && (
          <div className="mt-1.5 text-[11px] font-bold text-drelo-urgency animate-drelo-pulse">
            ⚠️ ¡Solo quedan {product.stock}!
          </div>
        )}

        <button
          onClick={handleAdd}
          disabled={sinPrecio}
          className={`mt-3 w-full py-2.5 rounded-lg font-bold text-sm uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${
            sinPrecio
              ? "bg-slate-100 text-drelo-muted cursor-not-allowed"
              : added ? "bg-drelo-mint text-white" : "bg-drelo-primary text-white hover:bg-drelo-hover active:scale-95"
          }`}
          data-testid={`add-to-cart-${product.id}`}
        >
          {sinPrecio ? "Próximamente" : added ? (<><Check className="w-4 h-4" /> Añadido</>) : "Añadir"}
        </button>
      </div>
    </Link>
  );
}
